import { Component, computed, inject, input, output, Signal, signal } from '@angular/core';
import { FFlowModule } from '@foblex/flow';
import { generateGuid } from '@foblex/utils';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideCheckSquare,
  lucideChevronDown,
  lucideGrid2x2,
  lucideMousePointerClick,
  lucideSquare,
  lucideTrash,
} from '@ng-icons/lucide';
import { TAILLE_NOEUD_DEFAULT } from '@shared/models/noeud.constants';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmContextMenuImports } from '@spartan-ng/helm/context-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

import { GroupeÉtapesÀLaCarte } from '../../models/groupe-étapes-à-la-carte.model';
import { Étape } from '../../models/étape.model';
import { EtapeStore } from '../../parcours-etape/store/etape.store';
import { GroupeÉtapesÀLaCarteStore } from '../../store/groupe-étapes-à-la-carte/groupe-étapes-à-la-carte.store';
import { ContextMenuComponent, MenuItem } from '../context-menu/context-menu.component';

@Component({
  selector: 'app-groupe-etapes-a-la-carte',
  imports: [
    FFlowModule,
    HlmIconImports,
    HlmButtonImports,
    HlmContextMenuImports,
    ContextMenuComponent,
    HlmSeparatorImports,
    HlmTooltipImports,
  ],
  templateUrl: './groupe-etapes-a-la-carte.component.html',
  styleUrls: ['./groupe-etapes-a-la-carte.component.scss'],
  providers: [
    provideIcons({
      lucideChevronDown,
      lucideGrid2x2,
      lucideSquare,
      lucideMousePointerClick,
      lucideCheckSquare,
      lucideTrash,
      lucideArrowRight,
    }),
  ],
})
export class GroupeEtapesALaCarteComponent {
  readonly #étapeStore = inject(EtapeStore);
  readonly #groupeÉtapesStore = inject(GroupeÉtapesÀLaCarteStore);

  readonly groupeEtapes = input.required<GroupeÉtapesÀLaCarte>();
  readonly peutModifier = input<boolean>(true);

  readonly showDetails = output();
  readonly centrerEtapeALaCarte = output<string>();
  readonly supprimerGroupe = output();

  readonly etapes = computed(() => this.#étapeStore.étapesActives());
  readonly height = computed(() =>
    this.estDeployee() ? 190 + this.groupeEtapes().etapes.length * 48 : this.TAILLE_NOEUD_DEFAULT.height,
  );
  readonly estDeployee = signal(false);

  protected readonly TAILLE_NOEUD_DEFAULT = TAILLE_NOEUD_DEFAULT;
  readonly id = generateGuid();

  toggleGroupe = () => this.estDeployee.update((x) => !x);

  nodeContextMenuItems: Signal<MenuItem<GroupeÉtapesÀLaCarte, Étape>[]> = computed(() => {
    const result: MenuItem<GroupeÉtapesÀLaCarte, Étape>[] = [
      {
        label: "Afficher les détails du groupe d'étapes",
        icon: 'lucideNotebookText',
        action: () => this.showDetails.emit(),
      },
      {
        label: "Déployer le contenu du groupe d'étapes",
        icon: 'lucideChevronDown',
        action: () => this.toggleGroupe(),
      },
    ];

    if (this.peutModifier()) {
      result.push(
        {
          label: 'Sélectionner les étapes à ajouter au groupe',
          icon: 'lucideMousePointerClick',
          children: this.etapes()
            .filter((x) => !!x.nom)
            .sort((a, b) => a.nom!.toLowerCase().localeCompare(b.nom!.toLowerCase()))
            .map<MenuItem<Étape>>((étape) => ({
              label: étape.nom!,
              type: 'checkbox',
              checked: this.groupeEtapes()
                .etapes.map((x) => x.id)
                .includes(étape.id),
              action: () => this.toggleEtape(étape),
            })),
        },
        {
          label: "Supprimer le groupe d'étapes",
          icon: 'lucideTrash',
          action: () => this.supprimerGroupe.emit(),
        },
      );
    }

    return result;
  });

  toggleEtape(étape: Étape | undefined) {
    if (!étape) return;

    const groupeÉtapes = this.groupeEtapes();

    this.#groupeÉtapesStore.toggleStep(étape, groupeÉtapes.id);
    this.#groupeÉtapesStore.modifier({
      idGroupe: groupeÉtapes.id,
      data: {
        nom: groupeÉtapes.nom,
        duree: groupeÉtapes.duree,
        idsEtapes: groupeÉtapes.etapes.map((x) => x.id),
      },
      next: () => {},
    });
  }

  centrerEtape(idEtape: string) {
    this.centrerEtapeALaCarte.emit(idEtape);
  }

  aDoublons() {
    const doublons = this.#groupeÉtapesStore
      .entities()
      .filter((x) => x.nom === this.groupeEtapes().nom && x.id !== this.groupeEtapes().id);
    return doublons.length > 0;
  }
}
